export function positivespline(x, y, u, args) {
  const zerothreshold = 1e-6;
  const log0 = (x) => Math.log(zerothreshold + Math.max(0, x));
  const exp0 = (x) => Math.max(0, Math.exp(x) - zerothreshold);
  cubicspline(x, y.map(log0), u, args).map(exp0);
}

// cubic Hermite spline
export function cubicspline(x, y, u, downwards) {
  let v = new Array(u.length);
  let j = 0;
  // assumptions
  // * x.length >= 4
  // * no duplicate x values
  // if (downwards)
  // * y is monotonically decreasing

  // Example
  // console.log({ x, y, u })
  // { x: [ 0.5, 1.5 ], y: [ 16, 8 ], u: [ 0.25, 0.75, 1.25, 1.75 ] }

  // continue constant slope at edges
  x = [100 * x[0] - 99 * x[1]].concat(x);
  y = [100 * y[0] - 99 * y[1]].concat(y);
  x = [2 * x[0] - x[1]].concat(x);
  y = [2 * y[0] - y[1]].concat(y);
  x = x.concat(100 * x[x.length - 1] - 99 * x[x.length - 2]);
  y = y.concat(100 * y[y.length - 1] - 99 * y[y.length - 2]);
  x = x.concat(2 * x[x.length - 1] - x[x.length - 2]);
  y = y.concat(2 * y[y.length - 1] - y[y.length - 2]);

  // console.log({ x, y, u })
  /*
    { x: [ -197.5, -98.5, 0.5, 1.5, 100.5, 199.5 ],
      y: [ 1600, 808, 16, 8, -784, -1576 ],
      u: [ 0.25, 0.75, 1.25, 1.75 ] }
    */

  // walk through all (x, y)
  for (let i = 0; i < x.length - 2; i++) {
    // jump forward in 'i' to the next point in 'u'
    while (i < x.length - 2 && x[i + 1] <= u[j]) i++;

    // Find slope of the (x,y) series around u[j]
    let dt1 = x[i] - x[i - 1],
      dt = x[i + 1] - x[i],
      dt2 = x[i + 2] - x[i + 1];
    let dydt1 = (y[i] - y[i - 1]) / dt1,
      dydt = (y[i + 1] - y[i]) / dt,
      dydt2 = (y[i + 2] - y[i + 1]) / dt2;
    //let m0 = (dydt1/dt1 + dydt)/(1/dt1 + 1)
    //    m1 = (dydt2/dt2 + dydt)/(1/dt2 + 1);

    let m0 = (dydt1 / dt1 + dydt / dt) / (1 / dt + 1 / dt1),
      m1 = (dydt2 / dt2 + dydt / dt) / (1 / dt + 1 / dt2);
    m0 *= dt;
    m1 *= dt;

    if (downwards) {
      m0 = Math.max(m0, 3 * dydt1, 3 * dydt); // limit m0 to not be too steep
      m0 = Math.min(m0, 0); // limit m0 to not start upwards
      m1 = Math.max(m1, 3 * dydt2, 3 * dydt); // limit m1 to not be too steep
      m1 = Math.min(m1, 0); // limit m1 to not end upwards
    }

    let p0 = y[i],
      p1 = y[i + 1];
    // loop while x[i] < u[j] <= x[i+1]
    for (; j < u.length && u[j] <= x[i + 1]; j++) {
      let t = Math.max(0, (u[j] - x[i]) / dt);
      let h00 = 2 * t * t * t - 3 * t * t + 1,
        h01 = t * t * t - 2 * t * t + t,
        h10 = -2 * t * t * t + 3 * t * t,
        h11 = t * t * t - t * t;
      v[j] = h00 * p0 + h01 * m0 + h10 * p1 + h11 * m1;
    }
  }
  return v;
}

const g10_1 = (t) => 30 * t * t * t * t - 60 * t * t * t + 30 * t * t;
//const g00_1 = (t) => -g10_1(1 - t);
const g11_1 = (t) => -15 * t * t * t * t + 28 * t * t * t - 12 * t * t;
const g01_1 = (t) => g11_1(1 - t);
const g12_1 = (t) => (5 / 2) * t * t * t * t - 4 * t * t * t + (3 / 2) * t * t;
const g02_1 = (t) => -g12_1(1 - t);

// y[i] specifies the integral from x[i-1] to x[i]
// y[0] specifies the integral from 0 to x[0]
// if fixedstart=true then the interpolation starts from y[0] at x[0]
// Somewhat similar to Cubic Hermite Splines if step size = 1
export function quadraticspline(x, y, u, fixedstart) {
  let v = new Array(u.length);
  let j = 0;

  const s4 = (A, B, D, E, edge) => {
    // Cap drops
    if (E < D * 0.75) E = D - (B - D);
    if (D < B * 0.75) {
      if (edge === 1) {
        D = B - (A - B);
        E = D - (A - B);
      } else {
        B = D - (E - D);
        A = B - (E - D);
      }
    }
    if (B < A * 0.75) {
      A = B + (B - D);
    }

    const ahat = (E - A - 3 * D + 3 * B) / 6;
    const bhat = (A + E - B - D) * 4;
    const chat = (E - A - 15 * D + 15 * B) / -12;
    const dhat = (A + E - 7 * B - 7 * D) / -12;

    return [ahat, bhat, chat, dhat];
  };

  const N = x.length;
  // continue constant acceleration at start and constant value at end
  const z = (i) =>
    i < 0 ? y[0] * Math.pow(y[1] / y[0], i) : i >= N ? y[N - 1] : y[i];
  const t = (i) =>
    i < 0
      ? x[0] + i * (x[1] - x[0])
      : i >= N
      ? x[N - 1] + (i - N + 1) * (x[N - 1] - x[N - 2])
      : x[i];

  // walk through all (x, y)
  let i = Math.min(0, Math.floor((u[0] - x[0]) / (x[1] - x[0])));
  for (; j < u.length; i++) {
    // jump forward in 'i' to the next point in 'u'
    while (t(i + 1) <= u[j]) i++;

    //console.log([i, j, z(i - 1), z(i), z(i + 1), z(i + 2)]);
    const s0 = s4(z(i - 1), z(i), z(i + 1), z(i + 2), 0);
    const s1 = s4(z(i), z(i + 1), z(i + 2), z(i + 3), 1);
    let m0 = s0[3];
    let m1 = s1[3];
    let k0 = s0[2] - 2 * s0[0];
    let k1 = s1[2] - 2 * s1[0];

    if (fixedstart) {
      if (i === 0) {
        m1 = m1 - (y[0] - m0) / 5;
        m0 = y[0];
      }
      if (i === 1) {
        const s0_0 = s4(z(0 - 1), z(0), z(0 + 1), z(0 + 2));
        const m0_0 = s0_0[3];
        m0 = m0 - (y[0] - m0_0) / 5;
      }
    }

    //let p0 = z(i);
    //let p1 = z(i) + z(i + 1);
    let p1 = z(i + 1);
    //let dt = t(i + 1) - t(i);

    // Find slope of the (x,y) series around u[j]
    let dt1 = t(i) - t(i - 1),
      dt = t(i + 1) - t(i),
      dt2 = t(i + 2) - t(i + 1);

    //p0 /= dt; // integrate to same value over distance != 1
    p1 /= dt;
    k0 *= (2 * dt) / (dt1 + dt); // Same effective slope even if dt1 ! = dt
    k1 *= (2 * dt) / (dt2 + dt);

    // loop while x[i] < u[j] <= x[i+1]
    for (; j < u.length && u[j] <= t(i + 1); j++) {
      let s = Math.max(0, (u[j] - t(i)) / dt);
      v[j] =
        //g00_1(s) * p0 +
        g01_1(s) * m0 +
        g02_1(s) * k0 +
        g10_1(s) * p1 +
        g11_1(s) * m1 +
        g12_1(s) * k1;
    }
  }
  return v;
}
